import React, { useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    TextField,
    CircularProgress,
    Typography,
    Box,
    makeStyles
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1)
    },
    successText: {
        color: theme.palette.success.main
    },
    processingText: {
        marginTop: theme.spacing(2)
    }
}));

const DateRangeDialog = ({ open, onClose, onExport, loading, jobStatus }) => {
    const classes = useStyles();
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [error, setError] = useState("");

    const validateDates = () => {
        if (!startDate || !endDate) {
            setError("Both start and end dates are required");
            return false;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            setError("Invalid date format");
            return false;
        }

        if (start > end) {
            setError("Start date cannot be after end date");
            return false;
        }

        setError("");
        return true;
    };

    const handleExport = () => {
        if (validateDates()) {
            const formattedStartDate = `${startDate}T00:00:00.000Z`;
            const formattedEndDate = `${endDate}T23:59:59.999Z`;

            onExport(formattedStartDate, formattedEndDate);
        }
    };

    const handleClose = () => {
        setStartDate("");
        setEndDate("");
        setError("");
        onClose();
    };

    return (
        <Dialog open={open} onClose={loading ? null : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Export Transactions by Date Range</DialogTitle>
            <DialogContent>
                {!loading && !jobStatus ? (
                    <>
                        <DialogContentText>
                            Select a date range to export transactions. Please note that large date ranges
                            may take some time to process.
                        </DialogContentText>
                        <TextField
                            margin="dense"
                            id="start-date"
                            label="Start Date"
                            type="date"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            id="end-date"
                            label="End Date"
                            type="date"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                        {error && (
                            <Typography className={classes.errorText} variant="body2">
                                {error}
                            </Typography>
                        )}
                    </>
                ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" py={3}>
                        {loading && (
                            <>
                                <CircularProgress />
                                <Typography variant="h6" className={classes.processingText}>
                                    Processing your request...
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    This may take a few minutes for large date ranges.
                                </Typography>
                            </>
                        )}

                        {jobStatus && (
                            <Box textAlign="center" width="100%">
                                <Typography variant="h6" gutterBottom>
                                    Report Status: {jobStatus.status}
                                </Typography>
                                {jobStatus.status === "failed" && (
                                    <Typography variant="body1" className={classes.errorText}>
                                        Error: {jobStatus.error || "An unknown error occurred"}
                                    </Typography>
                                )}
                                {jobStatus.status === "processing" && (
                                    <>
                                        <CircularProgress />
                                        <Typography variant="body1" className={classes.processingText}>
                                            Your report is being generated...
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!loading && !jobStatus && (
                    <>
                        <Button onClick={handleClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleExport} color="primary" variant="contained">
                            Export
                        </Button>
                    </>
                )}

                {jobStatus && jobStatus.status === "failed" && (
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                )}

                {(loading || (jobStatus && jobStatus.status === "processing")) && (
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DateRangeDialog;